
FWS.Product = (() => {

  const NORMALIZED_KEY = '__normalized__'
  const LOW_STOCK_LIMIT = 5
  const isNormalized = raw => raw && raw[NORMALIZED_KEY]

  const _this = {

    getStatusData(product, givenVariant) {
      const variant = givenVariant || product.variants[0]
      const status = { quantity: variant.quantityAvailable, sellBelowZero: variant.inventoryPolicy?.value === 'continue' }
      console.log('status', status)
      if (variant.availabilityOverride?.value)
        return { ...status, id: variant.availabilityOverride.value === 'force_allow' ? 'in-stock' : 'out-of-stock' }
      if (_this.isLowStock(variant))
        return { ...status, id: 'low-stock' }
      if (_this.isInStock(variant))
        return { ...status, id: 'in-stock' }
      if (_this.isBackorder(variant) && _this.isShowBuyButton(product))
        return { ...status, id: 'backorder', estimatedShipDate: _this.getEstimatedShipDate(variant) }
      if (_this.isOutOfStock(product, variant))
        return { ...status, id: 'out-of-stock' }
    },

    isInStock: variant => variant.quantityAvailable >= LOW_STOCK_LIMIT,
    isLowStock: variant => variant.quantityAvailable > 0 && variant.quantityAvailable < LOW_STOCK_LIMIT,
    isBackorder: variant => variant.currentlyNotInStock,
    isShowMAP: variant => variant.showMAP?.value == 'true',
    isAvailable: variant => variant.availableForSale,
    isShowBuyButton: product => product.showBuyButton?.value == 1,
    isCallToOrder: product => product.callToOrder?.value == 1,
    getCallToOrderLabel: product => product.callToOrderLabelOverride?.value || "Call to Order",
    isOutOfStock: (product, variant) => variant.quantityAvailable <= 0 && (!_this.isAvailable(variant) || !_this.isShowBuyButton(product)),
    isProp65: product => product.prop65?.value == 1,
    isNewArrival: product => new Date(product.createdAt).getTime() > (Date.now() - (1000*60*60*24*30)), // createdAt is less than 30 days old
    getEstimatedShipDate: (() => {
      const months = 'January Febuary March April May June July August September October November December'.split(' ')
      return variant => {
        if (!variant || !variant.estimatedShipDate) return null
        const raw = variant.estimatedShipDate.value
        const date = new Date(raw)
        const month = months[date.getUTCMonth()]
        const dateNumber = date.getUTCDate()
        return `${month} ${dateNumber}, ${date.getUTCFullYear()}`
      }
    })(),

    async getMerchandisingAlternative(product) {
      if (product.tags.includes('product_merchandised:No')) return

      let merch = []
      if (product.merchandisingStrategy?.value === 'cross_sell') {
        const handles = product.merchandisingCrossSellProducts?.value?.split(',').map(raw => ({ handle: raw.trim() }))
        const alts = await Promise.all(handles.map(_this.normalizeProduct))
        merch = alts.filter(alt => alt.variants[0].availableForSale)
      } else if (product.merchandisingStrategy?.value === 'switch_and_save') {
        const handles = product.similarItems.value.split(',').map(raw => ({ handle: raw.trim() }))
        const alts = await Promise.all(handles.map(_this.normalizeProduct))
        merch = alts.filter(alt => {
          return alt && (parseFloat(alt.variants[0].price) < parseFloat(product.variants[0].price))
        })
      }

      return merch[0] && {
        title: merch[0].title,
        handle: merch[0].handle,
        url: `/products/${merch[0].handle}`,
        variantId: merch[0].variants[0].id,
        sku: merch[0].variants[0].sku,
        price: merch[0].variants[0].price,
        image: merch[0].featuredImage.url,
        header: product.merchandisingMessaging?.value,
        merchandiseType:product.merchandisingStrategy?.value,
        status: _this.getStatusData(merch[0], merch[0].variants[0]),
        noPromoShipping: !_this.getsPromoShipping(merch[0].variants[0])
      }
    },

    getUnitPrice(variant) {
      let [ _, packInfo ] = variant.sku.split(':')
      if (!packInfo) packInfo = variant.fullRollLength?.value
      if (!packInfo) return variant.price
      return (parseFloat(_this.getPublicPrice(variant)) / parseInt(packInfo)).toFixed(2)
    },

    getPublicPrice(variant) {
      if (!isNormalized(variant)) return 0
      return _this.isShowMAP(variant) ? parseFloat(variant.MAP.value) : parseFloat(variant.price)
    },

    getsPromoShipping(variant) {
      return (variant && variant.shippingGroups) ? 
        variant.shippingGroups.value === 'PromoShipping' : false
    },

    isPricedByFoot(variant) {
      return /\b(ft|foot|feet)\b/.test(variant.title.toLowerCase());
    },

    isPricedByCubicFoot(variant) {
      return /\b(cu ft)\b/.test(variant.title.toLowerCase());
    },

    async normalizeProduct(raw) {
      if (isNormalized(raw)) return raw

      return _this.fetchProduct(
        raw.handle ? 
        { handle: raw.handle } : 
        { id: FWS.Api.storefrontId('Product', raw.id) }
      ).then(product => {
        if (!product) return null

        return {
          ...raw,
          ...product,
          price: raw.price && raw.price.toString(), // keep consistent with other API's
          media: product.media.edges.map(edge => edge.node),
          description: product.descriptionHtml,
          descriptionText: product.description,
          reviewAverage: product.reviewAverage?.value ? { value: parseFloat(product.reviewAverage.value) } : {},
          reviewCount: product.reviewCount?.value ? { value: parseInt(product.reviewCount.value) } : {},
          sellingPlans: product.sellingPlanGroups?.edges?.[0]?.node?.sellingPlans?.edges?.map(edge => edge.node),
          variants: product.variants.edges.map(edge => {
            const id = parseInt(edge.node.id.split('/').pop())
            const variant = (raw.variants && raw.variants.find(v => v.id === id)) || {}
            return {
              ...variant,
              ...edge.node,
              id,
              price: edge.node.price?.amount,
              compareAtPrice: edge.node.compareAtPrice?.amount,
              [NORMALIZED_KEY]: true
            }
          }),
          [NORMALIZED_KEY]: true
        }
      })
    },

    fetchProduct(config) {
      const [key, val] = Object.entries(config)[0]
      return FWS.Api.storefrontRequest(`
          query {
            product(${key}: "${val}") {
              id
              createdAt
              title
              handle
              totalInventory
              availableForSale
              tags
              vendor
              featuredImage { 
                url
                altText
              }
              media(first: 20) {
                edges {
                  node {
                    ... on MediaImage {
                      image {
                        url
                        altText
                      }
                    }
                    mediaContentType
                    previewImage {
                      url
                      altText
                    }
                    ... on ExternalVideo {
                      embedUrl
                    }
                  }
                }
              }
              variants(first: 100) {
                edges {
                  node {
                    id
                    title
                    quantityAvailable
                    availableForSale
                    currentlyNotInStock
                    promoType: metafield(namespace: "custom", key: "promo_type") {
                      value
                    }
                    msrp: metafield(namespace: "custom", key: "variant_msrp") {
                      value
                    }
                    buyBoxNote: metafield(namespace: "custom", key: "variant_buy_box_note") {
                      value
                    }
                    fullRollLength: metafield(namespace: "custom", key: "full_roll_length") {
                      value
                    }
                    gpItemClassCode: metafield(namespace: "custom", key: "item_class_code") {
                      value
                    }
                    shippingGroups: metafield(namespace: "global", key: "SHIPPING_GROUPS") {
                      value
                    }
                    estimatedShipDate: metafield(namespace: "inventory", key: "estimated_ship_date") {
                      value
                    }
                    inventoryPolicy: metafield(namespace: "custom", key: "inventory_policy") {
                      value
                    }
                    sku
                    price { amount }
                    compareAtPrice { amount }
                    availabilityOverride: metafield(namespace: "inventory", key: "availability_override") {
                      value
                    }
                    specifications: metafield(namespace: "custom", key :"product_specifications") {
                      value
                    }
                    shippingGroups: metafield(namespace: "global", key: "SHIPPING_GROUPS") {
                      value
                    }
                    showMAP: metafield(namespace: "products", key: "show_map_price") {
                      value
                    },
                    MAP: metafield(namespace: "products", key: "map_price") {
                      value
                    }
                  }
                }
              }
              descriptionHtml
              merchandisingStrategy: metafield(namespace: "custom", key: "merchandising_strategy") {
                value
              }
              merchandisingMessaging: metafield(namespace: "custom", key: "pop_up_box_messaging") {
                value
              }
              merchandisingCrossSellProducts: metafield(namespace: "custom", key: "cross_sell_products") {
                value
              }
              compatibilityTable: metafield(namespace: "products", key: "compatible") {
                value
              }
              prop65: metafield(namespace: "global", key: "CA_prop_65") {
                value
              }
              quoteFormEnabled: metafield(namespace: "custom", key: "allow_quote_request") {
                value
              }
              quoteFormMinimum: metafield(namespace: "custom", key: "minimum_quote_request_quantity") {
                value
              }
              callToOrder: metafield(namespace: "global", key: "call_to_order") {
                value
              }
              callToOrderLabelOverride: metafield(namespace: "global", key: "call_to_order_label_override") {
                value
              }
              similarItems: metafield(namespace: "parts_arrays", key: "similar_items") {
                value
              }
              showBuyButton: metafield(namespace: "global", key: "show_buy_button") {
                value
              }
              reviewAverage: metafield(namespace: "yotpo", key: "reviews_average") {
                value
              }
              reviewCount: metafield(namespace: "yotpo", key: "reviews_count") {
                value
              }
              sellingPlanGroups(first: 5) {
                edges {
                  node {
                    appName
                    name
                    options {
                      values
                    }
                    sellingPlans(first: 5) {
                      edges {
                        node {
                          id
                          name
                          description
                          priceAdjustments {
                            orderCount
                            adjustmentValue {
                              ... on SellingPlanPercentagePriceAdjustment {
                                adjustmentPercentage
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }`
      ).then(resp => resp?.data?.product)
    }
  }

  return _this
})()
